import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToTop } from '../../utils/scrollUtils';
import { useHelpCategories } from './Help';

/**
 * The Help section, compressed into the foot of the hero's left column:
 * "What's bothering you today?" over a row of five symptom tiles.
 *
 * It sits on the same off-white, dot-gridded surface as the product showcase
 * on the right, so the hero reads as three panels of one system — the red
 * message card above this, and the showcase beside both.
 *
 * Phones get the tiles as a swipeable row (five across would crush the
 * labels at 360px); from `sm` up they sit five across.
 *
 * `variant="glass"` is the desktop hero's version: a frosted dock laid over
 * the stage photo, with white type and translucent tiles that turn solid
 * white on hover.
 */
// Per-variant classes. `surface` is the default (sand panel).
const STYLES = {
  surface: {
    section: 'bg-[#F6F3F0] ring-1 ring-black/[0.04]',
    title: 'text-gray-900',
    highlight: 'text-brand',
    all: 'text-gray-500 hover:text-brand',
    tile: 'bg-white ring-1 ring-black/[0.05] shadow-[0_14px_30px_-24px_rgba(17,24,39,0.5)] hover:shadow-[0_20px_36px_-22px_rgb(var(--brand)/0.55)]',
    bubble: 'bg-[#F6F3F0] group-hover/tile:bg-brand/10',
    label: 'text-gray-800 group-hover/tile:text-brand',
  },
  glass: {
    section: 'bg-white/[0.12] ring-1 ring-inset ring-white/20 backdrop-blur-xl shadow-[0_20px_44px_-24px_rgba(0,0,0,0.6)]',
    title: 'text-white',
    highlight: 'text-[#C9EBC1]', // light mint: the logo green vanishes on the green scrim
    all: 'text-white/70 hover:text-white',
    // Short screens: label-only tiles, so the dock fits a 768px laptop.
    tile: 'bg-white/[0.14] ring-1 ring-inset ring-white/15 hover:bg-white hover:ring-transparent hover:shadow-[0_18px_36px_-18px_rgba(0,0,0,0.55)] [@media(max-height:820px)]:py-2',
    bubble: 'bg-white/20 group-hover/tile:bg-brand/10 [@media(max-height:820px)]:hidden',
    label: 'text-white group-hover/tile:text-brand',
  },
};

const HeroHelpStrip = ({ variant = 'surface' }) => {
  const glass = variant === 'glass';
  const ui = STYLES[glass ? 'glass' : 'surface'];
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { categories, handleCategoryClick } = useHelpCategories();

  return (
    <section
      className={`relative flex-shrink-0 overflow-hidden rounded-[2rem] px-[clamp(0.9rem,1.4vw,1.5rem)] py-[clamp(0.75rem,2vh,1.25rem)] ${ui.section}`}
      aria-label={`${t('home.help.title')} ${t('home.help.titleHighlight')}`}
    >
      {!glass && <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />}

      <div className="relative">
        <header className="mb-[clamp(0.5rem,1.4vh,0.85rem)] flex items-center justify-between gap-3">
          <h2 className={`truncate text-[clamp(0.95rem,2vh,1.15rem)] font-extrabold tracking-[-0.02em] ${ui.title}`}>
            {t('home.help.title')}{' '}
            <span className={ui.highlight}>{t('home.help.titleHighlight')}</span>
          </h2>
          <button
            type="button"
            onClick={() => { navigate('/products'); setTimeout(() => scrollToTop(), 100); }}
            className={`group/all inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:underline ${ui.all}`}
          >
            <span className="hidden xl:inline">{t('home.help.browseAll')}</span>
            <HiArrowRight className="h-3.5 w-3.5 transition-transform group-hover/all:translate-x-0.5" aria-hidden="true" />
          </button>
        </header>

        <div className="help-rail -mx-[clamp(0.9rem,1.4vw,1.5rem)] flex snap-x snap-mandatory gap-2 overflow-x-auto px-[clamp(0.9rem,1.4vw,1.5rem)] pb-1 sm:mx-0 sm:grid sm:grid-cols-5 sm:gap-[clamp(0.4rem,0.7vw,0.75rem)] sm:overflow-visible sm:px-0 sm:pb-0">
          <style>{`.help-rail { scrollbar-width: none; } .help-rail::-webkit-scrollbar { display: none; }`}</style>
          {categories.map((category, index) => (
            <motion.button
              key={category.id}
              type="button"
              onClick={() => handleCategoryClick(category.id)}
              title={category.description}
              className={`group/tile flex w-[5.9rem] min-w-0 flex-shrink-0 snap-start flex-col items-center sm:w-auto gap-[clamp(0.3rem,0.9vh,0.55rem)] rounded-[1.25rem] px-1.5 py-[clamp(0.55rem,1.5vh,0.9rem)] text-center transition-[background-color,box-shadow] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${ui.tile}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.97 }}
            >
              <span
                className={`flex h-[clamp(2rem,4.6vh,2.75rem)] w-[clamp(2rem,4.6vh,2.75rem)] items-center justify-center rounded-full text-[clamp(1.05rem,2.4vh,1.45rem)] transition-colors duration-300 ${ui.bubble}`}
                aria-hidden="true"
              >
                {category.emoji}
              </span>
              <span className={`line-clamp-2 w-full text-[clamp(10.5px,1.35vh,12.5px)] font-semibold leading-tight transition-colors duration-300 ${ui.label}`}>
                {category.title}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroHelpStrip;
